import { useState, useRef } from 'react'
import { Card, CardHeader, CardTitle, CardDescription, CardContent, Button, Input, Label, Textarea, Select } from '@/components/ui'
import { Receipt, Download, Plus, Trash2, Calculator } from 'lucide-react'
import { toast } from 'sonner'
import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'

interface InvoiceItem {
  id: string
  description: string
  quantity: number
  unitPrice: number
}

interface InvoiceData {
  invoiceNumber: string
  invoiceDate: string
  dueDate: string
  companyName: string
  companyAddress: string
  companyEmail: string
  companyPhone: string
  clientName: string
  clientAddress: string
  clientEmail: string
  items: InvoiceItem[]
  notes: string
  taxRate: number
  currency: string
}

export function InvoiceGenerator() {
  const invoiceRef = useRef<HTMLDivElement>(null)
  const [data, setData] = useState<InvoiceData>({
    invoiceNumber: `INV-${String(Math.floor(Math.random() * 9999)).padStart(4, '0')}`,
    invoiceDate: new Date().toISOString().split('T')[0],
    dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    companyName: 'Your Company Name',
    companyAddress: '123 Business Street, Manila, Philippines',
    companyEmail: 'billing@company.com',
    companyPhone: '+63 2 1234 5678',
    clientName: 'Client Company',
    clientAddress: '456 Client Avenue, Makati City, Philippines',
    clientEmail: 'client@example.com',
    items: [
      { id: '1', description: 'Web Development Services', quantity: 40, unitPrice: 1500 },
      { id: '2', description: 'UI/UX Design', quantity: 20, unitPrice: 1200 },
      { id: '3', description: 'Server Maintenance', quantity: 1, unitPrice: 5000 },
    ],
    notes: 'Payment is due within 30 days. Please include invoice number in your payment reference.',
    taxRate: 12,
    currency: 'PHP',
  })

  const updateField = (field: keyof InvoiceData, value: string | number | InvoiceItem[]) => {
    setData((prev) => ({ ...prev, [field]: value }))
  }

  const addItem = () => {
    const newItem: InvoiceItem = {
      id: crypto.randomUUID(),
      description: '',
      quantity: 1,
      unitPrice: 0,
    }
    setData((prev) => ({ ...prev, items: [...prev.items, newItem] }))
  }

  const updateItem = (id: string, field: keyof InvoiceItem, value: string | number) => {
    setData((prev) => ({
      ...prev,
      items: prev.items.map((item) =>
        item.id === id ? { ...item, [field]: value } : item
      ),
    }))
  }

  const removeItem = (id: string) => {
    setData((prev) => ({
      ...prev,
      items: prev.items.filter((item) => item.id !== id),
    }))
  }

  const calculateSubtotal = () => {
    return data.items.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0)
  }

  const calculateTax = () => {
    return calculateSubtotal() * (data.taxRate / 100)
  }

  const calculateTotal = () => {
    return calculateSubtotal() + calculateTax()
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: data.currency,
    }).format(amount)
  }

  const downloadPDF = async () => {
    if (!invoiceRef.current) return

    try {
      const canvas = await html2canvas(invoiceRef.current, {
        scale: 2,
        backgroundColor: '#ffffff',
        useCORS: true,
      })

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      })

      const imgWidth = 210
      const imgHeight = (canvas.height * imgWidth) / canvas.width

      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, imgWidth, imgHeight)
      pdf.save(`${data.invoiceNumber}.pdf`)
      toast.success('Invoice downloaded!')
    } catch {
      toast.error('Failed to generate PDF')
    }
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
            <Receipt className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Invoice Generator</h1>
            <p className="text-muted-foreground">Create professional invoices and download as PDF</p>
          </div>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[1fr_600px]">
        {/* Editor */}
        <div className="space-y-4">
          {/* Invoice Details */}
          <Card className="glass">
            <CardHeader>
              <CardTitle>Invoice Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Invoice Number</Label>
                  <Input
                    value={data.invoiceNumber}
                    onChange={(e) => updateField('invoiceNumber', e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Invoice Date</Label>
                  <Input
                    type="date"
                    value={data.invoiceDate}
                    onChange={(e) => updateField('invoiceDate', e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Due Date</Label>
                  <Input
                    type="date"
                    value={data.dueDate}
                    onChange={(e) => updateField('dueDate', e.target.value)}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Currency</Label>
                  <Select value={data.currency} onChange={(e) => updateField('currency', e.target.value)}>
                    <option value="PHP">PHP - Philippine Peso</option>
                    <option value="USD">USD - US Dollar</option>
                    <option value="EUR">EUR - Euro</option>
                    <option value="GBP">GBP - British Pound</option>
                    <option value="JPY">JPY - Japanese Yen</option>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Tax Rate (%)</Label>
                  <Input
                    type="number"
                    value={data.taxRate}
                    onChange={(e) => updateField('taxRate', Number(e.target.value))}
                    min="0"
                    max="100"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Company Info */}
          <Card className="glass">
            <CardHeader>
              <CardTitle>Your Company</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2 space-y-1">
                  <Label className="text-xs">Company Name</Label>
                  <Input
                    value={data.companyName}
                    onChange={(e) => updateField('companyName', e.target.value)}
                  />
                </div>
                <div className="col-span-2 space-y-1">
                  <Label className="text-xs">Address</Label>
                  <Input
                    value={data.companyAddress}
                    onChange={(e) => updateField('companyAddress', e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Email</Label>
                  <Input
                    type="email"
                    value={data.companyEmail}
                    onChange={(e) => updateField('companyEmail', e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Phone</Label>
                  <Input
                    value={data.companyPhone}
                    onChange={(e) => updateField('companyPhone', e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Client Info */}
          <Card className="glass">
            <CardHeader>
              <CardTitle>Bill To</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2 space-y-1">
                  <Label className="text-xs">Client Name</Label>
                  <Input
                    value={data.clientName}
                    onChange={(e) => updateField('clientName', e.target.value)}
                  />
                </div>
                <div className="col-span-2 space-y-1">
                  <Label className="text-xs">Address</Label>
                  <Input
                    value={data.clientAddress}
                    onChange={(e) => updateField('clientAddress', e.target.value)}
                  />
                </div>
                <div className="col-span-2 space-y-1">
                  <Label className="text-xs">Email</Label>
                  <Input
                    type="email"
                    value={data.clientEmail}
                    onChange={(e) => updateField('clientEmail', e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Items */}
          <Card className="glass">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Items</CardTitle>
                <Button variant="outline" size="sm" onClick={addItem}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Item
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {data.items.map((item, index) => (
                <div key={item.id} className="p-3 border border-border rounded-lg bg-muted/20">
                  <div className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-5 space-y-1">
                      <Label className="text-xs">Description</Label>
                      <Input
                        value={item.description}
                        onChange={(e) => updateItem(item.id, 'description', e.target.value)}
                        placeholder="Item description"
                      />
                    </div>
                    <div className="col-span-2 space-y-1">
                      <Label className="text-xs">Qty</Label>
                      <Input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => updateItem(item.id, 'quantity', Number(e.target.value))}
                        min="1"
                      />
                    </div>
                    <div className="col-span-3 space-y-1">
                      <Label className="text-xs">Unit Price</Label>
                      <Input
                        type="number"
                        value={item.unitPrice}
                        onChange={(e) => updateItem(item.id, 'unitPrice', Number(e.target.value))}
                        min="0"
                      />
                    </div>
                    <div className="col-span-2 flex items-center justify-between">
                      <span className="text-sm font-medium">
                        {formatCurrency(item.quantity * item.unitPrice)}
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive"
                        onClick={() => removeItem(item.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
              {data.items.length === 0 && (
                <p className="text-center text-muted-foreground py-4">No items added yet</p>
              )}

              {/* Totals */}
              <div className="pt-4 border-t border-border space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Subtotal</span>
                  <span>{formatCurrency(calculateSubtotal())}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Tax ({data.taxRate}%)</span>
                  <span>{formatCurrency(calculateTax())}</span>
                </div>
                <div className="flex justify-between text-lg font-bold pt-2 border-t border-border">
                  <span>Total</span>
                  <span className="text-primary">{formatCurrency(calculateTotal())}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Notes */}
          <Card className="glass">
            <CardHeader>
              <CardTitle>Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={data.notes}
                onChange={(e) => updateField('notes', e.target.value)}
                placeholder="Payment terms, bank details, etc."
                className="min-h-[80px]"
              />
            </CardContent>
          </Card>

          <Button onClick={downloadPDF} className="w-full">
            <Download className="h-4 w-4 mr-2" />
            Download Invoice PDF
          </Button>
        </div>

        {/* Preview */}
        <Card className="glass h-fit sticky top-4">
          <CardHeader>
            <CardTitle>Preview</CardTitle>
            <CardDescription>Live preview of your invoice</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="bg-muted/20 p-4 rounded-lg overflow-auto max-h-[900px]">
              <div
                ref={invoiceRef}
                className="bg-white text-black shadow-lg font-sans"
                style={{ width: '595px', minHeight: '842px', padding: '40px' }}
              >
                {/* Header */}
                <div className="flex justify-between items-start mb-8">
                  <div>
                    <h1 className="text-3xl font-bold text-gray-800">{data.companyName}</h1>
                    <p className="text-sm text-gray-600 mt-1">{data.companyAddress}</p>
                    <p className="text-sm text-gray-600">{data.companyEmail}</p>
                    <p className="text-sm text-gray-600">{data.companyPhone}</p>
                  </div>
                  <div className="text-right">
                    <h2 className="text-2xl font-bold text-teal-600">INVOICE</h2>
                    <p className="text-sm text-gray-600 mt-2">
                      <span className="font-semibold">Invoice #:</span> {data.invoiceNumber}
                    </p>
                    <p className="text-sm text-gray-600">
                      <span className="font-semibold">Date:</span>{' '}
                      {new Date(data.invoiceDate).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </p>
                    <p className="text-sm text-gray-600">
                      <span className="font-semibold">Due:</span>{' '}
                      {new Date(data.dueDate).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </p>
                  </div>
                </div>

                {/* Bill To */}
                <div className="mb-8 p-4 bg-gray-50 rounded">
                  <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">Bill To</h3>
                  <p className="font-semibold text-gray-800">{data.clientName}</p>
                  <p className="text-sm text-gray-600">{data.clientAddress}</p>
                  <p className="text-sm text-gray-600">{data.clientEmail}</p>
                </div>

                {/* Items Table */}
                <table className="w-full mb-8">
                  <thead>
                    <tr className="border-b-2 border-gray-800">
                      <th className="text-left py-3 text-sm font-semibold text-gray-600">Description</th>
                      <th className="text-center py-3 text-sm font-semibold text-gray-600 w-20">Qty</th>
                      <th className="text-right py-3 text-sm font-semibold text-gray-600 w-28">Unit Price</th>
                      <th className="text-right py-3 text-sm font-semibold text-gray-600 w-28">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((item) => (
                      <tr key={item.id} className="border-b border-gray-200">
                        <td className="py-3 text-sm">{item.description}</td>
                        <td className="py-3 text-sm text-center">{item.quantity}</td>
                        <td className="py-3 text-sm text-right">{formatCurrency(item.unitPrice)}</td>
                        <td className="py-3 text-sm text-right font-medium">
                          {formatCurrency(item.quantity * item.unitPrice)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Totals */}
                <div className="flex justify-end mb-8">
                  <div className="w-64">
                    <div className="flex justify-between py-2 text-sm">
                      <span className="text-gray-600">Subtotal</span>
                      <span>{formatCurrency(calculateSubtotal())}</span>
                    </div>
                    <div className="flex justify-between py-2 text-sm border-b border-gray-200">
                      <span className="text-gray-600">Tax ({data.taxRate}%)</span>
                      <span>{formatCurrency(calculateTax())}</span>
                    </div>
                    <div className="flex justify-between py-3 text-lg font-bold">
                      <span>Total</span>
                      <span className="text-teal-600">{formatCurrency(calculateTotal())}</span>
                    </div>
                  </div>
                </div>

                {/* Notes */}
                {data.notes && (
                  <div className="p-4 bg-gray-50 rounded">
                    <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">Notes</h3>
                    <p className="text-sm text-gray-600 whitespace-pre-wrap">{data.notes}</p>
                  </div>
                )}

                {/* Footer */}
                <div className="mt-8 pt-4 border-t border-gray-200 text-center">
                  <p className="text-xs text-gray-400">Thank you for your business!</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
